import { invalidate, useThree } from '@react-three/fiber';
import { useEffect, useLayoutEffect, useMemo, useRef } from 'react';
import { type BufferGeometry, Vector2 } from 'three';
import { LineMaterial } from 'three/examples/jsm/lines/LineMaterial.js';
import { LineSegments2 } from 'three/examples/jsm/lines/LineSegments2.js';
import { LineSegmentsGeometry } from 'three/examples/jsm/lines/LineSegmentsGeometry.js';
import { useShallow } from 'zustand/shallow';

import { EdgeLineThickness } from 'app/modules/wallforming/components/setting-container/types/graphicSettings';
import { usePlacementStore } from 'app/modules/wallforming/components/state-management/placement';

/** Interleaved buffer shape as exposed by LineSegmentsGeometry */
interface InterleavedBufferLike {
    array: Float32Array;
    needsUpdate: boolean;
}

const lineThicknessMap: Record<EdgeLineThickness, number> = {
    thin: 1.0,
    medium: 1.8,
    thick: 2.8
};

interface ArticleEdgeLinesProps {
    edgesGeometry?: BufferGeometry | null;
    edgeLineThickness: EdgeLineThickness;
    color?: number;
}

export const ArticleEdgeLines = ({ edgesGeometry, edgeLineThickness, color = 0x555555 }: ArticleEdgeLinesProps) => {
    const size = useThree((state) => state.size);

    const { edgesOverlayEnabled, aoQuality } = usePlacementStore(
        useShallow((state) => {
            return {
                edgesOverlayEnabled: state.graphicsSettings.edgesOverlayEnabled,
                aoQuality: state.graphicsSettings.aoQuality
            };
        })
    );

    const isEnabled = useMemo(
        () => edgesOverlayEnabled && aoQuality === 'off' && edgesGeometry !== null,
        [aoQuality, edgesGeometry, edgesOverlayEnabled]
    );

    const material = useMemo(
        () =>
            new LineMaterial({
                color: 0x777777,
                linewidth: lineThicknessMap.medium,
                resolution: new Vector2(1, 1),
                depthTest: true,
                depthWrite: false,
                transparent: true,
                toneMapped: false,
                polygonOffset: true,
                polygonOffsetFactor: -4,
                polygonOffsetUnits: -8
            }),
        []
    );

    const lineSegments = useMemo(() => {
        const instance = new LineSegments2(new LineSegmentsGeometry(), material);
        instance.renderOrder = 1;
        instance.visible = false;
        return instance;
    }, [material]);

    const lineSegmentsRef = useRef<LineSegments2>(lineSegments);
    const materialRef = useRef<LineMaterial>(material);

    useLayoutEffect(() => {
        materialRef.current.resolution.set(size.width, size.height);
    }, [size]);

    useLayoutEffect(() => {
        materialRef.current.linewidth = lineThicknessMap[edgeLineThickness];
        invalidate();
    }, [edgeLineThickness]);

    useLayoutEffect(() => {
        materialRef.current.color.setHex(color);
        invalidate();
    }, [color]);

    useLayoutEffect(() => {
        const currentLineSegments = lineSegmentsRef.current;

        if (isEnabled && edgesGeometry) {
            const posAttr = edgesGeometry.getAttribute('position');
            if (posAttr) {
                const positionsArray = posAttr.array as Float32Array;
                // instanceStart is an InterleavedBufferAttribute; .data is the shared
                // InterleavedBuffer that backs both instanceStart and instanceEnd.
                const interleavedBuffer = (
                    currentLineSegments.geometry.attributes['instanceStart'] as
                        | { data?: InterleavedBufferLike }
                        | undefined
                )?.data;

                if (interleavedBuffer && interleavedBuffer.array.length === positionsArray.length) {
                    // Same segment count → update in-place, no GPU reallocation
                    interleavedBuffer.array.set(positionsArray);
                    interleavedBuffer.needsUpdate = true;
                } else {
                    const geometry = new LineSegmentsGeometry();
                    geometry.setPositions(positionsArray);
                    currentLineSegments.geometry.dispose();
                    currentLineSegments.geometry = geometry;
                }
            }
        }

        currentLineSegments.visible = isEnabled && !!edgesGeometry;
        invalidate();
    }, [isEnabled, edgesGeometry]);

    useEffect(() => {
        const material = materialRef.current;
        return () => {
            lineSegments.geometry.dispose();
            material.dispose();
        };
    }, [lineSegments]);

    // <primitive> lets R3F manage scene.add / scene.remove automatically.
    return <primitive object={lineSegments} />;
};
